import React from 'react';
import cls from '@soluto-design/styles/cls';
import Link, { LinkProps } from '@soluto-design/link';
import { Text } from '@soluto-design/react';
import BlogShare from '../../molecules/BlogShare';
import BlockDemoSection, { BlockDemoSectionProps } from '../BlockDemoSection';
import logoNewEnverus from './logo-new-enverus.svg';

interface FooterLinkProps extends LinkProps {
    items?: LinkProps[];
}

export interface FooterProps {
    title: string;
    text?: string;
    twitterUrl?: string;
    linkedinUrl?: string;
    instagramUrl?: string;
    youtubeUrl?: string;
    links?: {
        items: FooterLinkProps[];
    }[];
    copyright?: string;
    bottomLinks?: LinkProps[];
    textLogoBottom?: string;
    logoBottomSrc?: string;
    demoSection?: BlockDemoSectionProps;
    demoSectionOnlyMobile?: boolean;
}

export default function Footer({
    title,
    text,
    twitterUrl,
    linkedinUrl,
    instagramUrl,
    youtubeUrl,
    links = [],
    copyright,
    bottomLinks,
    textLogoBottom,
    logoBottomSrc,
    demoSection,
    demoSectionOnlyMobile,
}: FooterProps) {
    const socialLinks = [
        { url: twitterUrl, icon: 'twitter', title: 'Twitter' },
        { url: linkedinUrl, icon: 'linkedin', title: 'LinkedIn' },
        { url: instagramUrl, icon: 'instagram', title: 'Instagram' },
        { url: youtubeUrl, icon: 'youtube', title: 'YouTube' },
    ].filter((link) => link.url);

    return (
        <footer data-theme="dark" className={cls('bg-background-primary')}>
            {!!demoSection && (
                <BlockDemoSection
                    {...demoSection}
                    {...(!!demoSectionOnlyMobile && {
                        className: 'lg:hidden',
                        title: undefined,
                        text: undefined,
                        anchor: true,
                    })}
                />
            )}
            <div className="container">
                <div className={cls('row pb-xxxl', !demoSection && 'pt-xxxl')}>
                    <div className="col-span-full md:col-span-2 xl:hidden mb-24 md:mb-0">
                        <img
                            src={logoNewEnverus}
                            alt="RatedPower a part of Enverus"
                            className="!h-40 lg:!h-48"
                        />
                    </div>
                    <div className="col-span-full md:col-span-4 md:col-start-5 lg:col-span-2 lg:col-start-7 xl:col-span-3 xl:col-start-1 xl:flex xl:flex-col xl:items-start">
                        <img
                            src={logoNewEnverus}
                            alt="RatedPower a part of Enverus"
                            className="mb-40 !h-48 hidden xl:block"
                        />
                        {text && (
                            <Text
                                isHtml
                                component="div"
                                className={cls(
                                    'text-b400 text-text-primary mb-24 font-regular [&_a]:!no-underline [&_a]:!font-regular',
                                )}>
                                {text}
                            </Text>
                        )}
                        {socialLinks && socialLinks.length > 0 && (
                            <div className="flex col-span-8 md:col-span-1 xl:col-span-2 pb-64 md:pb-56 xl:pb-0 xl:flex-1 xl:items-end">
                                <BlogShare links={socialLinks} />
                            </div>
                        )}
                    </div>
                    <div className="col-span-full xl:col-span-8 xl:col-start-5">
                        {links && links.length > 0 && (
                            <nav className="flex flex-col gap-gutter md:grid md:grid-cols-8">
                                {links?.map((column, i) => (
                                    <div
                                        key={`column-${i}`}
                                        className="col-span-2">
                                        {column.items?.map((link, i) => (
                                            <div
                                                key={`link-${i}`}
                                                className="flex flex-col gap-8 pb-gutter last:pb-0">
                                                <Link
                                                    href={link.href}
                                                    target={link.target}
                                                    title={link.title}
                                                    className="block text-b400 text-text-primary !font-semibold !no-underline">
                                                    {link.children}
                                                </Link>
                                                {link.items &&
                                                    link.items.length > 0 && (
                                                        <ul className="flex flex-col gap-8">
                                                            {link.items?.map(
                                                                (
                                                                    subLink,
                                                                    i,
                                                                ) => (
                                                                    <li
                                                                        key={`subLink-${i}`}>
                                                                        <Link
                                                                            href={
                                                                                subLink?.href
                                                                            }
                                                                            target={
                                                                                subLink?.target
                                                                            }
                                                                            className="block !text-b400 !font-regular !text-text-secondary hover:!text-text-primary !no-underline">
                                                                            {
                                                                                subLink.children
                                                                            }
                                                                        </Link>
                                                                    </li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </nav>
                        )}
                    </div>
                </div>
                <div className="row border-t-small border-stroke-secondary pt-12 pb-lg">
                    {copyright && (
                        <div className="lg:flex lg:items-center col-span-full md:col-span-4 lg:col-span-2 xl:col-span-3 pb-20 md:pb-0">
                            <Text
                                isHtml
                                className="!text-c100 text-text-primary">
                                {copyright}
                            </Text>
                        </div>
                    )}
                    {bottomLinks && bottomLinks.length > 0 && (
                        <div className="col-span-full md:col-span-4 lg:col-span-2 xl:col-span-3 xl:col-start-5 flex gap-20 pb-20 lg:pb-0 lg:flex lg:items-center">
                            {bottomLinks?.map((link, index) => (
                                <Link
                                    key={`bottomLink-${index}`}
                                    href={link?.href}
                                    className="block !text-c100 text-text-primary !no-underline">
                                    {link.children}
                                </Link>
                            ))}
                        </div>
                    )}
                    {(logoBottomSrc || textLogoBottom) && (
                        <div className="col-span-full md:col-span-4 md:col-start-5 xl:col-start-9 flex gap-8 items-center">
                            {logoBottomSrc && (
                                <img
                                    loading="lazy"
                                    src={logoBottomSrc}
                                    width={35}
                                    alt={title ? title : ''}
                                    className="h-24"
                                />
                            )}
                            {textLogoBottom && (
                                <Text
                                    isHtml
                                    component="div"
                                    className={cls(
                                        'text-[8px] leading-normal text-text-secondary',
                                    )}>
                                    {textLogoBottom}
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
}
